// components/admin/BusinessVerifications.jsx
import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

const BusinessVerifications = () => {
    const [verifications, setVerifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedVerification, setSelectedVerification] = useState(null);
    const [reviewNotes, setReviewNotes] = useState('');
    const [actionLoading, setActionLoading] = useState(null);

    useEffect(() => {
        fetchVerifications();
    }, []);

    const fetchVerifications = async () => {
        try {
            const response = await api.get(API_CONFIG.ADMIN.BUSINESS_VERIFICATIONS);
            if (response.data.status === 'success') {
                setVerifications(response.data.data);
            }
        } catch (error) {
            setError('Failed to fetch verification requests');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (id, status) => {
        setActionLoading(id);
        try {
            const response = await api.put(`/admin/business-verifications/${id}`, {
                status,
                notes: reviewNotes
            });

            if (response.data.status === 'success') {
                // Update local state
                setVerifications(prev => prev.map(verification => 
                    verification.id === id 
                        ? { 
                            ...verification, 
                            status, 
                            notes: reviewNotes,
                            reviewed_at: new Date().toISOString()
                          } 
                        : verification
                ));
                setSelectedVerification(null);
                setReviewNotes('');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Action failed');
        } finally {
            setActionLoading(null);
        }
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            pending: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800'
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
                {status?.charAt(0)?.toUpperCase() + status?.slice(1) || 'Pending'}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Business Verification Requests</h1>
                <p className="text-gray-600">Review and approve business verification requests</p>
            </div>

            {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Business
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    RC Number
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    User
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Submitted
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {verifications.map((verification) => (
                                <tr key={verification.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {verification.company_name}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {verification.rc_number}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {verification.first_name} {verification.last_name}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {verification.company_email}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {getStatusBadge(verification.status)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(verification.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        {verification.status === 'pending' && (
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => setSelectedVerification(verification)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                >
                                                    Review
                                                </button>
                                            </div>
                                        )}
                                        {verification.status !== 'pending' && (
                                            <span className="text-gray-500">Reviewed</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {verifications.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-gray-500">No verification requests found</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Review Modal */}
            {selectedVerification && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                Review Business Verification
                            </h3>
                            
                            <div className="mb-4">
                                <h4 className="font-semibold">Business Details:</h4>
                                <p><strong>Name:</strong> {selectedVerification.company_name}</p>
                                <p><strong>RC Number:</strong> {selectedVerification.rc_number}</p>
                                
                                <p><strong>User:</strong> {selectedVerification.fullname} ({selectedVerification.company_name})</p>
                                <p><strong>Email:</strong> {selectedVerification.company_email}</p>

                                <p><a href={selectedVerification.certificate_url} target="_blank" rel="noopener noreferrer">
                                    <button className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                                        View Certificate
                                        </button></a></p>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Review Notes
                                </label>
                                <textarea
                                    value={reviewNotes}
                                    onChange={(e) => setReviewNotes(e.target.value)}
                                    rows="4"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Add notes for the business owner..."
                                />
                            </div>

                            <div className="flex justify-end space-x-3 mt-6">
                                <button
                                    onClick={() => {
                                        setSelectedVerification(null);
                                        setReviewNotes('');
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleAction(selectedVerification.id, 'rejected')}
                                    disabled={actionLoading === selectedVerification.id}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                                >
                                    {actionLoading === selectedVerification.id ? 'Processing...' : 'Reject'}
                                </button>
                                <button
                                    onClick={() => handleAction(selectedVerification.id, 'approved')}
                                    disabled={actionLoading === selectedVerification.id}
                                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
                                >
                                    {actionLoading === selectedVerification.id ? 'Processing...' : 'Approve'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BusinessVerifications;